/**
 * Modal donde el director llena el formulario de entrega para una actividad
 * y sube archivos. Soporta editar mientras `canDirectorEdit` lo permita.
 */

import { useEffect, useMemo, useState } from "react";
import { Modal, ModalFooter, Button, Input } from "@/components/ui";
import { toast } from "react-hot-toast";
import { Timestamp } from "firebase/firestore";
import {
  saveSubmission,
  uploadSubmissionFile,
  deleteSubmissionFile,
  validateSubmissionValues,
  canDirectorEdit,
  getDisplaySubmissionStatus,
  toJsDate,
} from "@/lib/services/activitySubmissions";
import { useNow } from "@/hooks/useNow";
import type {
  ActivitySubmissionField,
  ClubActivitySubmission,
  EventActivity,
  SubmissionFieldValue,
  SubmissionFileRef,
} from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  activity: EventActivity;
  clubId: string;
  clubName: string;
  associationId?: string;
  existing?: ClubActivitySubmission;
}

export function SubmissionFormModal({
  isOpen,
  onClose,
  eventId,
  activity,
  clubId,
  clubName,
  associationId,
  existing,
}: Props) {
  const req = activity.submissionRequirements;
  const fields = useMemo(
    () => (req?.fields ?? []).slice().sort((a, b) => a.order - b.order),
    [req?.fields],
  );
  const [values, setValues] = useState<Record<string, SubmissionFieldValue>>(
    {},
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  // Pantalla de confirmación tras envío exitoso (no borrador).
  const [submittedConfirmation, setSubmittedConfirmation] = useState<{
    isLate: boolean;
    fileCount: number;
    fieldCount: number;
  } | null>(null);
  // Borramos archivos de Storage solo cuando se confirma el guardado.
  // Mientras tanto los acumulamos aquí para no dejar referencias "muertas"
  // si el usuario cancela.
  const [pendingFileDeletes, setPendingFileDeletes] = useState<string[]>([]);

  // Re-evaluamos el gate cada 30s para reaccionar a apertura/cierre.
  useNow(30_000);
  const editPermission = canDirectorEdit(activity, existing);

  useEffect(() => {
    if (existing) {
      setValues({ ...existing.values });
    } else {
      // Inicializar valores por defecto
      const init: Record<string, SubmissionFieldValue> = {};
      fields.forEach((f) => {
        if (f.type === "boolean") init[f.id] = false;
        else if (f.type === "multi_file" || f.type === "multi_select")
          init[f.id] = [];
        else init[f.id] = "";
      });
      setValues(init);
    }
    setErrors({});
    setPendingFileDeletes([]);
    setSubmittedConfirmation(null);
  }, [existing, isOpen, fields]);

  if (!req || !req.enabled) return null;

  const setVal = (id: string, v: SubmissionFieldValue) => {
    setValues((p) => ({ ...p, [id]: v }));
    setErrors((p) => {
      const { [id]: _, ...rest } = p;
      return rest;
    });
  };

  const handleFileUpload = async (
    field: ActivitySubmissionField,
    files: FileList | null,
  ) => {
    if (!files || files.length === 0) return;
    // Validación de tamaño y MIME antes de subir.
    const MAX_BYTES = 25 * 1024 * 1024; // 25 MB
    const allowed = field.acceptMimeTypes ?? [];
    const matchesMime = (file: File): boolean => {
      if (allowed.length === 0) return true;
      return allowed.some((pat) => {
        const p = pat.trim();
        if (!p) return false;
        if (p.endsWith("/*")) {
          return file.type.startsWith(p.slice(0, -1));
        }
        if (p.startsWith(".")) {
          return file.name.toLowerCase().endsWith(p.toLowerCase());
        }
        return file.type === p;
      });
    };
    for (const f of Array.from(files)) {
      if (f.size > MAX_BYTES) {
        toast.error(
          `"${f.name}" supera el límite de 25 MB (${(f.size / 1024 / 1024).toFixed(1)} MB)`,
        );
        return;
      }
      if (!matchesMime(f)) {
        toast.error(
          `"${f.name}" no tiene un formato permitido. Aceptados: ${allowed.join(", ")}`,
        );
        return;
      }
    }
    setUploading((p) => ({ ...p, [field.id]: true }));
    try {
      if (field.type === "file") {
        const uploaded = await uploadSubmissionFile(
          eventId,
          activity.id,
          clubId,
          files[0],
        );
        setVal(field.id, uploaded);
      } else if (field.type === "multi_file") {
        const max = field.maxFiles ?? 10;
        const current = (values[field.id] as SubmissionFileRef[]) ?? [];
        const remaining = max - current.length;
        const toUpload = Array.from(files).slice(0, remaining);
        const uploaded: SubmissionFileRef[] = [];
        for (const f of toUpload) {
          uploaded.push(
            await uploadSubmissionFile(eventId, activity.id, clubId, f),
          );
        }
        setVal(field.id, [...current, ...uploaded]);
      }
      toast.success("Archivo subido");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading((p) => ({ ...p, [field.id]: false }));
    }
  };

  const removeFile = (
    field: ActivitySubmissionField,
    target?: SubmissionFileRef,
  ) => {
    if (field.type === "file") {
      const cur = values[field.id] as SubmissionFileRef | undefined;
      if (cur?.path) {
        setPendingFileDeletes((p) => [...p, cur.path]);
      }
      setVal(field.id, "");
    } else if (field.type === "multi_file" && target) {
      setPendingFileDeletes((p) => [...p, target.path]);
      const cur = (values[field.id] as SubmissionFileRef[]) ?? [];
      setVal(
        field.id,
        cur.filter((f) => f.path !== target.path),
      );
    }
  };

  const handleSave = async (asDraft: boolean) => {
    if (!editPermission.allowed) {
      toast.error(editPermission.reason ?? "No se puede editar");
      return;
    }
    if (!asDraft) {
      const errs = validateSubmissionValues(fields, values);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        toast.error("Completa los campos requeridos");
        return;
      }
      // Aviso si la entrega quedará marcada como tarde.
      const dl = toJsDate(req.deadline);
      if (dl && Date.now() > dl.getTime() && req.allowLate) {
        const ok = window.confirm(
          "Pasó la fecha límite. La entrega quedará marcada como TARDE. ¿Deseas continuar?",
        );
        if (!ok) return;
      }
    }
    setSaving(true);
    try {
      await saveSubmission({
        eventId,
        activityId: activity.id,
        activityName: activity.name,
        categoryId: activity.categoryId,
        clubId,
        clubName,
        associationId,
        values,
        status: asDraft ? "draft" : "submitted",
        deadline: req.deadline,
        timingBands: activity.scoreConfig?.timingBands,
        isFirstSubmission: !existing,
      });
      // Tras escribir Firestore, recogemos los archivos pendientes de borrar.
      // Los errores de Storage se loguean sin romper el flujo.
      await Promise.all(pendingFileDeletes.map((p) => deleteSubmissionFile(p)));
      setPendingFileDeletes([]);
      if (asDraft) {
        toast.success("Borrador guardado");
        onClose();
      } else {
        // Mostrar pantalla de confirmación con resumen y opción de cerrar.
        const dl = toJsDate(req.deadline);
        const isLate = !!(dl && Date.now() > dl.getTime());
        let fileCount = 0;
        fields.forEach((f) => {
          const v = values[f.id];
          if (f.type === "file" && v && typeof v === "object" && "url" in v) {
            fileCount += 1;
          } else if (f.type === "multi_file" && Array.isArray(v)) {
            fileCount += v.length;
          }
        });
        const fieldCount = fields.filter((f) => {
          const v = values[f.id];
          if (v === undefined || v === null || v === "") return false;
          if (Array.isArray(v) && v.length === 0) return false;
          return true;
        }).length;
        setSubmittedConfirmation({ isLate, fileCount, fieldCount });
        toast.success("Entrega enviada");
      }
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        submittedConfirmation
          ? "✅ Entrega recibida"
          : `Entrega: ${activity.name}`
      }
      description={
        submittedConfirmation
          ? `Tu entrega de "${activity.name}" fue registrada correctamente.`
          : existing
            ? `Última actualización por ${existing.lastEditedByName ?? "—"}`
            : "Completa el formulario y sube los archivos requeridos"
      }
      size="lg"
    >
      {submittedConfirmation ? (
        <div className="space-y-4">
          <div className="flex flex-col items-center text-center py-6">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <svg
                className="w-9 h-9 text-emerald-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              ¡Envío confirmado!
            </h3>
            <p className="text-sm text-gray-600 mt-1 max-w-md">
              Hemos recibido tu entrega para <strong>{activity.name}</strong>{" "}
              del club <strong>{clubName}</strong>.
              {submittedConfirmation.isLate && (
                <>
                  {" "}
                  <span className="text-amber-700">
                    Quedó marcada como entrega tardía.
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">
                {submittedConfirmation.fieldCount}
              </p>
              <p className="text-xs text-gray-500">campo(s) llenados</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">
                {submittedConfirmation.fileCount}
              </p>
              <p className="text-xs text-gray-500">archivo(s) subido(s)</p>
            </div>
          </div>
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-900">
            <p className="font-medium mb-1">¿Y ahora?</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-800">
              <li>
                Los administradores revisarán tu entrega y aprobarán el puntaje.
              </li>
              <li>Recibirás una notificación cuando sea revisada.</li>
              {editPermission.allowed && (
                <li>Aún puedes editarla mientras la entrega esté abierta.</li>
              )}
            </ul>
          </div>
          <ModalFooter>
            <Button onClick={onClose}>Cerrar</Button>
          </ModalFooter>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <SubmissionHeader
              existing={existing}
              deadline={req.deadline}
              allowLate={req.allowLate}
              fields={fields}
              values={values}
            />
            <TimingBandsPreview
              bands={activity.scoreConfig?.timingBands}
              existing={existing}
            />
            {req.instructions && (
              <details
                className="text-sm bg-blue-50 border border-blue-200 rounded p-3"
                open
              >
                <summary className="font-medium text-blue-900 cursor-pointer">
                  Instrucciones
                </summary>
                <div className="mt-2 text-gray-700 whitespace-pre-wrap">
                  {req.instructions}
                </div>
              </details>
            )}
            {!editPermission.allowed && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {editPermission.reason}. Puedes consultar pero no editar.
              </div>
            )}
            {fields.map((f) => (
              <FieldRenderer
                key={f.id}
                field={f}
                value={values[f.id]}
                error={errors[f.id]}
                disabled={!editPermission.allowed || saving}
                uploading={uploading[f.id]}
                onChange={(v) => setVal(f.id, v)}
                onFileSelect={(files) => handleFileUpload(f, files)}
                onRemoveFile={(target) => removeFile(f, target)}
              />
            ))}
          </div>
          <ModalFooter>
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cerrar
            </Button>
            {editPermission.allowed && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => handleSave(true)}
                  loading={saving}
                >
                  Guardar borrador
                </Button>
                <Button onClick={() => handleSave(false)} loading={saving}>
                  {existing && existing.status === "submitted"
                    ? "Re-enviar"
                    : "Enviar"}
                </Button>
              </>
            )}
          </ModalFooter>
        </>
      )}
    </Modal>
  );
}

// ============================================================================
// Preview de bandas timing — muestra al director cuántos puntos recibe
// según la fecha de entrega.
// ============================================================================

function TimingBandsPreview({
  bands,
  existing,
}: {
  bands?: { label: string; points: number; untilDate?: Timestamp }[];
  existing?: ClubActivitySubmission;
}) {
  // Tick cada 30s para que la banda activa se actualice sin refresh.
  useNow(30_000);
  if (!bands || bands.length === 0) return null;
  const hasDated = bands.some((b) => b.untilDate);
  if (!hasDated) return null;
  const nowMs = Date.now();
  const activeIdx = (() => {
    if (existing && typeof existing.timingBandIndex === "number") {
      return existing.timingBandIndex;
    }
    const idx = bands.findIndex((b) => {
      const ms = toJsDate(b.untilDate)?.getTime();
      return ms !== undefined && nowMs <= ms;
    });
    return idx >= 0 ? idx : bands.length - 1;
  })();
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
      <div className="font-medium text-amber-900 mb-2">
        Puntaje según fecha de entrega
      </div>
      <ul className="space-y-1">
        {bands.map((b, i) => {
          const dl = toJsDate(b.untilDate);
          const isActive = i === activeIdx;
          return (
            <li
              key={i}
              className={`flex items-center justify-between gap-2 px-2 py-1 rounded ${
                isActive
                  ? "bg-white border border-amber-400 font-semibold"
                  : "text-gray-700"
              }`}
            >
              <span className="flex-1 truncate">
                {isActive && "→ "}
                {b.label}
                {dl && (
                  <span className="text-xs text-gray-500 ml-2">
                    hasta {dl.toLocaleString("es-CO")}
                  </span>
                )}
                {!dl && (
                  <span className="text-xs text-gray-500 ml-2">
                    (fuera de fechas)
                  </span>
                )}
              </span>
              <span className="text-amber-900 tabular-nums">
                {b.points} pts
              </span>
            </li>
          );
        })}
      </ul>
      {!existing && (
        <p className="text-[11px] text-amber-800 mt-2">
          Si entregas ahora recibirás{" "}
          <strong>{bands[activeIdx]?.points ?? 0} puntos</strong>.
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Header con estado, progreso y countdown
// ============================================================================

function SubmissionHeader({
  existing,
  deadline,
  allowLate,
  fields,
  values,
}: {
  existing?: ClubActivitySubmission;
  deadline?: Timestamp;
  allowLate?: boolean;
  fields: ActivitySubmissionField[];
  values: Record<string, SubmissionFieldValue>;
}) {
  const display = existing ? getDisplaySubmissionStatus(existing) : "draft";
  // Progreso: cuántos requeridos completos / total requeridos
  const requiredFields = fields.filter((f) => f.required);
  const requiredFilled = requiredFields.filter((f) => {
    const v = values[f.id];
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    if (typeof v === "object" && !Array.isArray(v) && !("url" in v))
      return false;
    return true;
  }).length;
  const progressPct =
    requiredFields.length === 0
      ? 100
      : Math.round((requiredFilled / requiredFields.length) * 100);

  // Countdown - tick cada minuto para refrescar la etiqueta. #10
  const dl = toJsDate(deadline);
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!dl) return;
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [dl]);
  let timing: { label: string; tone: "ok" | "warn" | "late" | "none" } = {
    label: "",
    tone: "none",
  };
  if (dl) {
    const diff = dl.getTime() - now;
    if (diff < 0) {
      const days = Math.floor(-diff / 86400000);
      timing = {
        label: `Pasó hace ${days} día(s)${allowLate ? " · entrega tardía permitida" : ""}`,
        tone: "late",
      };
    } else {
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      timing = {
        label: `Faltan ${days}d ${hours}h`,
        tone: days < 2 ? "warn" : "ok",
      };
    }
  }

  const statusStyle: Record<
    string,
    { bg: string; text: string; label: string }
  > = {
    draft: { bg: "bg-gray-100", text: "text-gray-700", label: "Borrador" },
    submitted: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      label: "Enviado · pendiente de revisión",
    },
    approved: { bg: "bg-green-100", text: "text-green-800", label: "Aprobado" },
    rejected: { bg: "bg-red-100", text: "text-red-800", label: "Rechazado" },
  };
  const st = statusStyle[display] ?? statusStyle.draft;

  const timingStyle = {
    ok: "bg-green-50 text-green-700 border-green-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    late: "bg-red-50 text-red-700 border-red-200",
    none: "",
  }[timing.tone];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`px-2 py-1 rounded text-xs font-semibold ${st.bg} ${st.text}`}
        >
          {st.label}
        </span>
        {existing?.isLate && (
          <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-100 text-amber-800">
            Tarde
          </span>
        )}
        {timing.label && (
          <span
            className={`px-2 py-1 rounded text-xs border ${timingStyle}`}
            title={dl ? dl.toLocaleString("es-CO") : undefined}
          >
            {timing.label}
          </span>
        )}
      </div>
      {dl && (
        <p className="text-xs text-gray-500">
          Fecha límite: {dl.toLocaleString("es-CO")}
          {allowLate && " · entregas tardías permitidas"}
        </p>
      )}
      {display === "rejected" && existing?.reviewNotes && (
        <div className="text-sm bg-red-50 border border-red-200 rounded p-3">
          <p className="font-medium text-red-900 mb-1">Motivo del rechazo:</p>
          <p className="text-red-800 whitespace-pre-wrap">
            {existing.reviewNotes}
          </p>
          <p className="text-xs text-red-700 mt-1">
            Corrige los puntos indicados y vuelve a enviar.
          </p>
        </div>
      )}
      {requiredFields.length > 0 && (
        <div>
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>
              Progreso: {requiredFilled} / {requiredFields.length} campos
              requeridos
            </span>
            <span className="font-medium">{progressPct}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${
                progressPct === 100 ? "bg-green-500" : "bg-purple-500"
              }`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Renderer de un campo
// ============================================================================

function FieldRenderer({
  field,
  value,
  error,
  disabled,
  uploading,
  onChange,
  onFileSelect,
  onRemoveFile,
}: {
  field: ActivitySubmissionField;
  value: SubmissionFieldValue;
  error?: string;
  disabled?: boolean;
  uploading?: boolean;
  onChange: (v: SubmissionFieldValue) => void;
  onFileSelect: (files: FileList | null) => void;
  onRemoveFile: (target?: SubmissionFileRef) => void;
}) {
  const labelEl = (
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {field.label}
      {field.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );
  const helpEl = field.helpText && (
    <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
  );
  const errEl = error && <p className="mt-1 text-xs text-red-600">{error}</p>;

  switch (field.type) {
    case "text_short":
      return (
        <div>
          {labelEl}
          <Input
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {helpEl}
          {errEl}
        </div>
      );
    case "text_long":
      return (
        <div>
          {labelEl}
          <textarea
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
            rows={field.rows ?? 3}
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {helpEl}
          {errEl}
        </div>
      );
    case "number":
      return (
        <div>
          {labelEl}
          <Input
            type="number"
            min={field.min}
            max={field.max}
            step={field.step ?? 1}
            value={(value as number | string) ?? ""}
            onChange={(e) =>
              onChange(e.target.value === "" ? "" : Number(e.target.value))
            }
            disabled={disabled}
          />
          {helpEl}
          {errEl}
        </div>
      );
    case "boolean":
      return (
        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
              className="rounded"
            />
            <span className="text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </span>
          </label>
          {helpEl}
          {errEl}
        </div>
      );
    case "select":
      return (
        <div>
          {labelEl}
          <select
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          >
            <option value="">Selecciona…</option>
            {(field.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {helpEl}
          {errEl}
        </div>
      );
    case "multi_select": {
      const arr = (value as string[]) ?? [];
      return (
        <div>
          {labelEl}
          <div className="flex flex-wrap gap-2">
            {(field.options ?? []).map((o) => (
              <label
                key={o}
                className="flex items-center gap-1 text-sm cursor-pointer px-2 py-1 border rounded"
              >
                <input
                  type="checkbox"
                  checked={arr.includes(o)}
                  disabled={disabled}
                  onChange={(e) => {
                    if (e.target.checked) onChange([...arr, o]);
                    else onChange(arr.filter((x) => x !== o));
                  }}
                />
                {o}
              </label>
            ))}
          </div>
          {helpEl}
          {errEl}
        </div>
      );
    }
    case "date":
      return (
        <div>
          {labelEl}
          <input
            type="date"
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {helpEl}
          {errEl}
        </div>
      );
    case "url":
      return (
        <div>
          {labelEl}
          <Input
            type="url"
            placeholder="https://…"
            value={(value as string) ?? ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
          />
          {helpEl}
          {errEl}
        </div>
      );
    case "file": {
      const file = value as SubmissionFileRef | undefined;
      return (
        <div>
          {labelEl}
          {file && typeof file === "object" && "url" in file ? (
            <div className="flex items-center gap-2 p-2 border border-gray-200 rounded text-sm">
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline flex-1 truncate"
              >
                📎 {file.name}
              </a>
              <button
                type="button"
                disabled={disabled}
                onClick={() => onRemoveFile()}
                className="text-red-600 text-xs"
              >
                Quitar
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept={field.acceptMimeTypes?.join(",")}
              disabled={disabled || uploading}
              onChange={(e) => onFileSelect(e.target.files)}
              className="text-sm"
            />
          )}
          <p className="text-xs text-gray-500 mt-1">
            Máximo 25 MB
            {field.acceptMimeTypes?.length
              ? ` · Formatos: ${field.acceptMimeTypes.join(", ")}`
              : ""}
          </p>
          {uploading && <p className="text-xs text-blue-600 mt-1">Subiendo…</p>}
          {helpEl}
          {errEl}
        </div>
      );
    }
    case "multi_file": {
      const files = (value as SubmissionFileRef[]) ?? [];
      const max = field.maxFiles ?? 10;
      return (
        <div>
          {labelEl}
          <div className="space-y-1 mb-2">
            {files.map((f) => (
              <div
                key={f.path}
                className="flex items-center gap-2 p-1.5 border border-gray-200 rounded text-sm"
              >
                <a
                  href={f.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex-1 truncate"
                >
                  📎 {f.name}
                </a>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onRemoveFile(f)}
                  className="text-red-600 text-xs"
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
          {files.length < max && (
            <input
              type="file"
              multiple
              accept={field.acceptMimeTypes?.join(",")}
              disabled={disabled || uploading}
              onChange={(e) => onFileSelect(e.target.files)}
              className="text-sm"
            />
          )}
          {uploading && <p className="text-xs text-blue-600 mt-1">Subiendo…</p>}
          <p className="text-xs text-gray-400 mt-1">
            {files.length} / {max} archivos · máx 25 MB c/u
            {field.acceptMimeTypes?.length
              ? ` · Formatos: ${field.acceptMimeTypes.join(", ")}`
              : ""}
          </p>
          {helpEl}
          {errEl}
        </div>
      );
    }
    default:
      return null;
  }
}
